import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  createPath,
  useLocation,
  useNavigate,
  UNSAFE_RouteContext as RouteContext,
  type To,
} from 'react-router-dom';
import { Skeleton, Notification } from '@telus-uds/components-web';
import { loadLegacyShare } from './legacyReact';

/**
 * Mounts a legacy-React remote in a root of its own, rendered by the React the legacy
 * remotes share rather than the shell's. Nothing crosses the boundary but plain props.
 *
 * The old shell wrapped every page in a router and a UDS BaseProvider, so where a remote
 * shares those packages it is re-wrapped here with its own copies. The router is driven by
 * the shell's location and navigate, keeping one source of truth for the URL. The shell
 * route's match is handed down as the remote's parent route, as the shared router used to,
 * so its relative <Routes> and links still resolve under the shell route.
 */

type LegacyReact = typeof React;
type LegacyRoot = { render(children: unknown): void; unmount(): void };
type LegacyReactDom = { createRoot(container: Element): LegacyRoot };
type LegacyRuntime = {
  React: LegacyReact;
  ReactDOM: LegacyReactDom;
  Router?: React.ComponentType<any>;
  RouteContext?: React.Context<unknown>;
  BaseProvider?: React.ComponentType<any>;
  theme?: unknown;
};

const withDefault = <T,>(mod: any): T => mod?.default ?? mod;

const loadLegacyRuntime = async (): Promise<LegacyRuntime> => {
  const [react, reactDom, router, uds, theme] = await Promise.all([
    loadLegacyShare<any>('react'),
    loadLegacyShare<any>('react-dom'),
    loadLegacyShare<any>('react-router-dom'),
    loadLegacyShare<any>('@telus-uds/components-web'),
    loadLegacyShare<any>('@telus-uds/theme-allium'),
  ]);
  if (!react || !reactDom?.createRoot) {
    throw new Error('this remote does not share react and react-dom');
  }
  return {
    React: withDefault<LegacyReact>(react),
    ReactDOM: reactDom,
    Router: router?.Router,
    RouteContext: router?.UNSAFE_RouteContext,
    BaseProvider: theme ? uds?.BaseProvider : undefined,
    theme: withDefault(theme),
  };
};

type LegacyReactIslandProps = {
  name: string;
  component: React.ComponentType<any>;
  props: Record<string, unknown>;
  /** A legacy-React provider to wrap the component in, inside the island. */
  provider?: { component: React.ComponentType<any>; props: Record<string, unknown> };
};

export const LegacyReactIsland = ({ name, component, props, provider }: LegacyReactIslandProps) => {
  const container = useRef<HTMLDivElement>(null);
  const root = useRef<LegacyRoot>(undefined);
  const [legacy, setLegacy] = useState<LegacyRuntime>();
  const [error, setError] = useState<Error>();
  const location = useLocation();
  const navigate = useNavigate();
  // No public API exposes the enclosing route's match (useMatches needs a data router), so
  // this reads react-router internals. The ~ range on react-router-dom keeps it to patches.
  const { matches } = useContext(RouteContext);
  const shellMatch = matches[matches.length - 1];

  useEffect(() => {
    loadLegacyRuntime().then(setLegacy, setError);
  }, []);

  useLayoutEffect(() => {
    if (!legacy || !container.current) return undefined;
    root.current = legacy.ReactDOM.createRoot(container.current);
    return () => {
      root.current?.unmount();
      root.current = undefined;
    };
  }, [legacy]);

  useLayoutEffect(() => {
    if (!legacy || !root.current) return;
    const { React: R, Router, RouteContext: LegacyRouteContext, BaseProvider, theme } = legacy;

    let tree = R.createElement(component, props);
    if (provider) tree = R.createElement(provider.component, provider.props, tree);
    if (BaseProvider) tree = R.createElement(BaseProvider, { defaultTheme: theme }, tree);
    if (LegacyRouteContext && shellMatch) {
      const { params, pathname, pathnameBase, route } = shellMatch;
      const parentMatch = { params, pathname, pathnameBase, route: { path: route.path } };
      tree = R.createElement(
        LegacyRouteContext.Provider,
        { value: { outlet: null, matches: [parentMatch], isDataRoute: false } },
        tree,
      );
    }
    if (Router) {
      const navigator = {
        createHref: (to: To) => (typeof to === 'string' ? to : createPath(to)),
        push: (to: To, state?: unknown) => navigate(to, { state }),
        replace: (to: To, state?: unknown) => navigate(to, { replace: true, state }),
        go: (delta: number) => navigate(delta),
      };
      tree = R.createElement(Router, { location, navigator }, tree);
    }
    root.current.render(tree);
  });

  if (error) {
    return (
      <Notification variant={{ style: 'error' }}>
        Error loading {name}: {error.message}
      </Notification>
    );
  }

  return (
    <>
      {!legacy && <Skeleton />}
      <div ref={container} />
    </>
  );
};
